import cn from "@/utils/cn";
import Link, { LinkProps } from "next/link";
import { BlogPostDocumentData } from "../../../../../prismicio-types";
import {
  DateField,
  PrismicDocumentHeader,
  RichTextField,
} from "@prismicio/client";
import { PrismicImage, PrismicRichText } from "@prismicio/react";
import TextComponent from "@/app/_components/TextComponent";

type DocumentHeader = PrismicDocumentHeader<"blog_post", string>;

type BlogListItemProps = {
  coverImage: BlogPostDocumentData["hero_image"];
  blogTitle: RichTextField;
  publishDate: DocumentHeader["last_publication_date"];
  blogLink: DocumentHeader["url"];
  blogExcerpt: RichTextField;
  blogDate: DateField;
} & React.HTMLAttributes<LinkProps>;

export default function BlogListItem({
  coverImage,
  blogTitle,
  publishDate,
  blogLink,
  blogExcerpt,
  blogDate,
  className,
  ...props
}: BlogListItemProps) {
  // Convert publishDate to a Date, then make it write like "January 22, 2024";
  const formattedDate = new Date(blogDate || publishDate).toLocaleDateString(
    "en-US",
    {
      month: "long",
      day: "numeric",
      year: "numeric",
    },
  );

  const transformText = (text: any, truncateLength = 200) => {
    if (text.length > truncateLength) {
      return text.substring(0, truncateLength) + "...";
    }
    return text;
  };

  return (
    // @ts-expect-error TODO: I'm sure this has some solution, but later.
    <Link
      href={blogLink || "/#"}
      className={cn(
        "group flex w-full flex-col group rounded-[15px] hover:bg-white transition-all hover:shadow-sm md:flex-row hover:border border border-transparent hover:border-kd-lte-blue",
        className,
      )}
      {...props}
    >
      {/* Image */}
      <div className="relative mx-2 hidden h-48 w-full shrink-0 self-center md:h-32 md:w-48">
        <PrismicImage
          field={coverImage}
          alt=""
          className="size-full rounded-lg object-cover"
        />
        <div className="absolute inset-0 hidden rounded-lg bg-kd-lte-blue opacity-30 group-hover:block"></div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col justify-between">
        <div className="px-[36px] py-5">
          {/* Title */}
          <PrismicRichText
            field={blogTitle}
            components={{
              paragraph: ({ children, text }) => (
                <TextComponent
                  type="h3"
                  className="text-[#2B3640] group-hover:text-kd-lte-blue"
                  title={children}
                  // className="text-xl font-semibold leading-tight text-kd-lte-blue group-hover:text-kd-lte-blue/80 md:text-2xl"
                >
                  {transformText(text, 80)}
                </TextComponent>
              ),
            }}
          />

          {/* Excerpt */}
          <PrismicRichText
            field={blogExcerpt}
            components={{
              paragraph: ({ children, text }) => (
                <TextComponent
                  type="body"
                  className="py-2 leading-relaxed text-kd-secondary-3"
                >
                  {transformText(text, 250)}
                </TextComponent>
              ),
            }}
          />
        </div>
      </div>
    </Link>
  );
}
